import type { paths } from '@snaphire/api-client';
import { field, type GetPath, type Row } from './api/client';
import type { Action } from './forms';
import { applyBackendFeatures } from './backend-features';
export type Section = 'recruitment' | 'organisation' | 'hr' | 'performance' | 'admin' | 'people';
export type Resource = {
  hidden?: boolean;
  parentScoped?: string;
  readPermissions?: string[];
  children?: (row: Row) => ResourceChild[];
  employeeScoped?: boolean;
  key: string;
  section: Section;
  title: string;
  singular: string;
  path: GetPath;
  detail?: GetPath;
  idParam?: string;
  readPermission: string;
  managePermission?: string;
  createPermission?: string;
  editPermission?: string;
  create?: keyof paths;
  edit?: keyof paths;
  canEdit?: (row: Row) => boolean;
  fields: string[];
  statusKey?: string;
  description: string;
  actions?: (row: Row) => Action[];
};
export type ResourceChild = {
  key: string;
  title: string;
  path: GetPath;
  params?: Record<string, string>;
  query?: Record<string, string>;
  collection?: string;
  referencePath?: GetPath;
  permission?: string;
  fields: string[];
  targetResource?: string;
  actions?: (child: Row, parent: Row) => Action[];
};
const action = (
  row: Row,
  label: string,
  path: keyof paths,
  permission: string,
  initial: Record<string, unknown> = {},
): Action => ({
  label,
  path,
  params: {
    id: row.id,
    employeeId: row.id,
    accountId: row.id,
    groupId: row.id,
    organizationId: row.id,
    departmentId: row.id,
    teamId: row.id,
  },
  permission,
  initial: { version: row.version, ...initial },
});
const approvals = (
  row: Row,
  prefix: '/api/v1/requisitions' | '/api/v1/job-postings' | '/api/v1/offers',
  manage: string,
  approve: string,
): Action[] => {
  const status = field(row, 'approvalStatus');
  const base = prefix as string;
  const items: Action[] = [];
  if (['DRAFT', 'REJECTED'].includes(status))
    items.push(
      action(
        row,
        'На согласование',
        `${base}/${'{id}'}/${prefix.endsWith('requisitions') ? 'submit' : 'submit-approval'}` as keyof paths,
        manage,
      ),
    );
  if (status === 'PENDING_APPROVAL' && row.canApprove === true)
    items.push(
      action(row, 'Согласовать', `${base}/{id}/approve` as keyof paths, approve),
      action(row, 'Отклонить', `${base}/{id}/reject` as keyof paths, approve),
    );
  return items;
};
const catalog = (key: string, title: string, singular: string): Resource => ({
  key,
  section: 'organisation',
  title,
  singular,
  path: `/api/v1/${key}` as GetPath,
  detail: `/api/v1/${key}/{id}` as GetPath,
  create: `/api/v1/${key}` as keyof paths,
  edit: `/api/v1/${key}/{id}` as keyof paths,
  fields: [
    'name',
    'code',
    'organizationId',
    ...(key === 'seniorities'
      ? ['rank']
      : key === 'locations'
        ? ['countryCode', 'timezone']
        : key === 'specialisations'
          ? ['jobFunctionId', 'ownerEmployeeId']
          : ['ownerEmployeeId']),
    'status',
  ],
  readPermission: `${key}.read`,
  managePermission: 'organization.manage',
  createPermission: `${key}.create`,
  editPermission: `${key}.edit`,
  description: `Справочник для профилей сотрудников и подбора персонала: ${title.toLowerCase()}.`,
});
export const resources: Resource[] = [
  {
    key: 'employees',
    section: 'people',
    title: 'Сотрудники',
    singular: 'Сотрудник',
    path: '/api/v1/employees',
    detail: '/api/v1/employees/{employeeId}',
    idParam: 'employeeId',
    create: '/api/v1/employees',
    edit: '/api/v1/employees/{employeeId}/work-profile',
    readPermission: 'employees.read',
    managePermission: 'employees.manage',
    createPermission: 'employees.create',
    editPermission: 'employees.edit',
    fields: ['workName', 'workEmail', 'jobTitle', 'departmentId', 'status'],
    description: 'Рабочие профили сотрудников.',
  },
  {
    key: 'requisitions',
    section: 'recruitment',
    title: 'Заявки на найм',
    singular: 'Заявка на найм',
    path: '/api/v1/requisitions',
    detail: '/api/v1/requisitions/{id}',
    create: '/api/v1/requisitions',
    edit: '/api/v1/requisitions/{id}',
    readPermission: 'requisitions.read',
    managePermission: 'requisitions.manage',
    fields: [
      'title',
      'departmentId',
      'approvalStatus',
      'lifecycleStatus',
      'targetStartDate',
      'recruiterId',
    ],
    statusKey: 'approvalStatus',
    description:
      'Запрашивайте штатные единицы, назначайте участников найма и получайте согласование.',
    actions: (row) => [
      ...approvals(row, '/api/v1/requisitions', 'requisitions.manage', 'requisitions.approve'),
      ...(field(row, 'lifecycleStatus') === 'OPEN'
        ? [
            action(row, 'Приостановить', '/api/v1/requisitions/{id}/hold', 'requisitions.manage'),
            action(row, 'Закрыть заявку', '/api/v1/requisitions/{id}/close', 'requisitions.manage'),
          ]
        : field(row, 'lifecycleStatus') === 'ON_HOLD'
          ? [
              action(
                row,
                'Возобновить найм',
                '/api/v1/requisitions/{id}/resume',
                'requisitions.manage',
              ),
            ]
          : []),
    ],
  },
  {
    key: 'job-postings',
    section: 'recruitment',
    title: 'Вакансии',
    singular: 'Вакансия',
    path: '/api/v1/job-postings',
    detail: '/api/v1/job-postings/{id}',
    create: '/api/v1/job-postings',
    edit: '/api/v1/job-postings/{id}',
    readPermission: 'job_postings.read',
    managePermission: 'job_postings.manage',
    fields: [
      'internalTitle',
      'publicTitle',
      'approvalStatus',
      'publicationStatus',
      'lifecycleStatus',
      'recruiterId',
    ],
    statusKey: 'lifecycleStatus',
    description: 'Управляйте вакансиями, формами отклика и воронками найма.',
    actions: (row) => [
      ...approvals(row, '/api/v1/job-postings', 'job_postings.manage', 'job_postings.approve'),
      action(
        row,
        'Форма отклика',
        '/api/v1/job-postings/{id}/application-form-versions',
        'job_postings.manage',
      ),
      action(
        row,
        'Воронка найма',
        '/api/v1/job-postings/{id}/pipeline-versions',
        'job_postings.manage',
      ),
      ...(Array.isArray(row.publishableChannels) && row.publishableChannels.length > 0
        ? [
            action(
              row,
              'Опубликовать вакансию',
              '/api/v1/job-postings/{id}/publish',
              'job_postings.publish',
            ),
          ]
        : []),
      ...(Array.isArray(row.unpublishableChannels) && row.unpublishableChannels.length > 0
        ? [
            action(
              row,
              'Снять с публикации',
              '/api/v1/job-postings/{id}/unpublish',
              'job_postings.publish',
            ),
          ]
        : []),
    ],
  },
  {
    key: 'candidates',
    section: 'recruitment',
    title: 'Кандидаты',
    singular: 'Кандидат',
    path: '/api/v1/candidates',
    detail: '/api/v1/candidates/{id}',
    create: '/api/v1/candidates',
    edit: '/api/v1/candidates/{id}',
    readPermission: 'candidates.read',
    managePermission: 'candidates.manage',
    fields: ['name', 'primaryEmail', 'country', 'source', 'processingStatus', 'currentEmployer'],
    statusKey: 'processingStatus',
    description: 'Храните профили кандидатов, цели обработки данных и отклики в одном месте.',
    actions: (row) => [
      {
        label: 'Добавить отклик',
        path: '/api/v1/applications',
        initial: { candidateId: row.id },
        permission: 'candidates.manage',
      },
      ...(Array.isArray(row.processingOptions) && row.processingOptions.length > 0
        ? [
            {
              ...action(
                row,
                'Добавить цель обработки',
                '/api/v1/candidates/{id}/processing-purposes',
                'candidates.manage',
                {
                  source: String((row.processingOptions[0] as Row).source),
                  purpose: '',
                  jurisdiction: '',
                },
              ),
              processingOptions: row.processingOptions as Action['processingOptions'],
              choices: {
                '/api/v1/privacy-notice-versions': (row.privacyNoticeOptions || []) as Row[],
              },
            },
          ]
        : []),
    ],
  },
  {
    key: 'applications',
    section: 'recruitment',
    title: 'Отклики',
    singular: 'Отклик',
    path: '/api/v1/applications',
    detail: '/api/v1/applications/{id}',
    create: '/api/v1/applications',
    readPermission: 'candidates.read',
    managePermission: 'candidates.manage',
    fields: ['candidateId', 'jobPostingId', 'status', 'currentStageId', 'createdAt'],
    statusKey: 'status',
    description: 'Продвигайте кандидатов по воронке, назначайте интервью и готовьте офферы.',
    actions: (row) => [
      ...(field(row, 'status') === 'ACTIVE'
        ? [
            action(
              row,
              'Сменить этап',
              '/api/v1/applications/{id}/change-stage',
              'candidates.manage',
            ),
            action(
              row,
              'Назначить интервью',
              '/api/v1/applications/{id}/interviews',
              'interviews.schedule',
            ),
            action(row, 'Создать оффер', '/api/v1/applications/{id}/offers', 'offers.manage'),
            action(
              row,
              'Отклонить отклик',
              '/api/v1/applications/{id}/reject',
              'candidates.manage',
            ),
          ]
        : []),
      ...(['ACTIVE', 'OFFER'].includes(field(row, 'status'))
        ? [
            action(
              row,
              'Отозвать отклик',
              '/api/v1/applications/{id}/withdraw',
              'candidates.manage',
            ),
          ]
        : []),
      ...(row.canHire === true
        ? [
            {
              ...action(
                row,
                'Нанять кандидата',
                '/api/v1/applications/{id}/hire',
                'candidates.manage',
                {
                  applicationVersion: row.version,
                },
              ),
              requiredPermissions: ['employees.create'],
            },
          ]
        : []),
    ],
  },
  {
    key: 'interviews',
    section: 'recruitment',
    title: 'Интервью',
    singular: 'Интервью',
    path: '/api/v1/interviews',
    detail: '/api/v1/interviews/{id}',
    readPermission: 'interviews.read',
    fields: [
      'applicationId',
      'startsAt',
      'durationMinutes',
      'status',
      'interviewerIds',
      'meetingUrl',
    ],
    statusKey: 'status',
    description: 'Назначайте интервью из карточки отклика и фиксируйте результат.',
    actions: (row) =>
      field(row, 'status') === 'SCHEDULED'
        ? [
            action(
              row,
              'Перенести интервью',
              '/api/v1/interviews/{id}/reschedule',
              'interviews.schedule',
            ),
            action(
              row,
              'Завершить интервью',
              '/api/v1/interviews/{id}/complete',
              'interviews.schedule',
            ),
            action(row, 'Неявка', '/api/v1/interviews/{id}/no-show', 'interviews.schedule'),
            action(
              row,
              'Отменить интервью',
              '/api/v1/interviews/{id}/cancel',
              'interviews.schedule',
            ),
            action(
              row,
              'Добавить обратную связь',
              '/api/v1/interviews/{id}/feedback',
              'interviews.feedback.own',
            ),
          ]
        : [],
  },
  {
    key: 'offers',
    section: 'recruitment',
    title: 'Офферы',
    singular: 'Оффер',
    path: '/api/v1/offers',
    detail: '/api/v1/offers/{id}',
    readPermission: 'offers.read',
    fields: ['applicationId', 'approvalStatus', 'lifecycleStatus', 'expiresAt', 'decidedAt'],
    statusKey: 'lifecycleStatus',
    description: 'Создавайте и согласовывайте офферы, отслеживайте решения кандидатов.',
    actions: (row) => [
      ...approvals(row, '/api/v1/offers', 'offers.manage', 'offers.approve'),
      ...(row.canSend === true
        ? [action(row, 'Отправить оффер', '/api/v1/offers/{id}/send', 'offers.manage')]
        : []),
      ...(['NOT_SENT', 'SENT', 'VIEWED', 'DELIVERY_FAILED'].includes(field(row, 'lifecycleStatus'))
        ? [action(row, 'Отозвать оффер', '/api/v1/offers/{id}/withdraw', 'offers.manage')]
        : []),
    ],
  },
  {
    key: 'headcount-allocations',
    section: 'recruitment',
    title: 'Штатный план',
    singular: 'Штатная единица',
    path: '/api/v1/headcount-allocations',
    create: '/api/v1/headcount-allocations',
    readPermission: 'requisitions.read',
    managePermission: 'requisitions.manage',
    fields: ['code', 'departmentId', 'status', 'employeeId', 'requisitionId'],
    description: 'Планируйте штатные единицы для заявок на найм.',
  },
  {
    key: 'onboarding',
    section: 'hr',
    title: 'Онбординг',
    singular: 'Процесс онбординга',
    path: '/api/v1/onboarding-processes',
    detail: '/api/v1/onboarding-processes/{id}',
    create: '/api/v1/onboarding-processes',
    readPermission: 'onboarding.read',
    managePermission: 'onboarding.manage',
    fields: [
      'employeeId',
      'status',
      'expectedStartDate',
      'hrOwnerId',
      'managerId',
      'progressPercent',
    ],
    statusKey: 'status',
    description:
      'Подготовьте новых сотрудников к первому рабочему дню и выполните задачи адаптации.',
    actions: (row) => [
      ...(field(row, 'status') === 'PENDING'
        ? [
            action(
              row,
              'Начать онбординг',
              '/api/v1/onboarding-processes/{id}/start',
              'onboarding.manage',
            ),
          ]
        : []),
      ...(row.canComplete === true
        ? [
            action(
              row,
              'Завершить онбординг',
              '/api/v1/onboarding-processes/{id}/complete',
              'onboarding.manage',
            ),
          ]
        : []),
      ...(['PENDING', 'IN_PROGRESS'].includes(field(row, 'status'))
        ? [
            action(
              row,
              'Отменить онбординг',
              '/api/v1/onboarding-processes/{id}/cancel',
              'onboarding.manage',
            ),
          ]
        : []),
    ],
  },
  {
    key: 'offboarding',
    section: 'hr',
    title: 'Офбординг',
    singular: 'Процесс офбординга',
    path: '/api/v1/offboarding-processes',
    detail: '/api/v1/offboarding-processes/{id}',
    create: '/api/v1/offboarding-processes',
    readPermission: 'offboarding.read',
    managePermission: 'offboarding.manage',
    fields: [
      'employeeId',
      'status',
      'lastWorkingDate',
      'accessEndsAt',
      'hrOwnerId',
      'progressPercent',
    ],
    statusKey: 'status',
    description: 'Координируйте увольнение, передачу дел и отключение доступа сотрудников.',
    actions: (row) => [
      ...(field(row, 'status') === 'PENDING'
        ? [
            action(
              row,
              'Начать офбординг',
              '/api/v1/offboarding-processes/{id}/start',
              'offboarding.manage',
            ),
          ]
        : []),
      ...(row.canComplete === true
        ? [
            action(
              row,
              'Завершить офбординг',
              '/api/v1/offboarding-processes/{id}/complete',
              'offboarding.manage',
            ),
          ]
        : []),
      ...(['PENDING', 'IN_PROGRESS'].includes(field(row, 'status'))
        ? [
            action(
              row,
              'Отменить офбординг',
              '/api/v1/offboarding-processes/{id}/cancel',
              'offboarding.manage',
            ),
            action(
              row,
              'Продлить доступ',
              '/api/v1/offboarding-processes/{id}/access-extension',
              'offboarding.access.manage',
            ),
          ]
        : []),
    ],
  },
  {
    key: 'templates',
    section: 'hr',
    title: 'Шаблоны процессов',
    singular: 'Шаблон процесса',
    path: '/api/v1/process-templates',
    create: '/api/v1/process-templates',
    readPermission: 'onboarding.read',
    managePermission: 'onboarding.manage',
    fields: ['name', 'type', 'status', 'createdAt'],
    statusKey: 'status',
    description:
      'Создавайте шаблоны HR-процессов с этапами, исполнителями, сроками и зависимостями задач.',
    actions: (row) => [
      action(
        row,
        'Создать версию шаблона',
        '/api/v1/process-templates/{id}/versions',
        field(row, 'type') === 'OFFBOARDING' ? 'offboarding.manage' : 'onboarding.manage',
      ),
    ],
  },
  {
    key: 'organizations',
    section: 'organisation',
    title: 'Организации',
    singular: 'Организация',
    path: '/api/v1/organizations',
    detail: '/api/v1/organizations/{organizationId}',
    idParam: 'organizationId',
    create: '/api/v1/organizations',
    edit: '/api/v1/organizations/{organizationId}',
    readPermission: 'organization.read',
    managePermission: 'organization.manage',
    fields: ['name', 'status', 'createdAt'],
    statusKey: 'status',
    description: 'Управляйте организацией в рабочем пространстве.',
  },
  {
    key: 'departments',
    section: 'organisation',
    title: 'Отделы',
    singular: 'Отдел',
    path: '/api/v1/departments',
    detail: '/api/v1/departments/{departmentId}',
    idParam: 'departmentId',
    create: '/api/v1/departments',
    edit: '/api/v1/departments/{departmentId}',
    readPermission: 'departments.read',
    managePermission: 'organization.manage',
    createPermission: 'departments.create',
    editPermission: 'departments.edit',
    fields: ['name', 'code', 'organizationId', 'ownerEmployeeId', 'status'],
    statusKey: 'status',
    description: 'Создавайте отделы и назначайте ответственных.',
  },
  {
    key: 'teams',
    section: 'organisation',
    title: 'Проекты',
    singular: 'Проект',
    path: '/api/v1/teams',
    detail: '/api/v1/teams/{teamId}',
    idParam: 'teamId',
    create: '/api/v1/teams',
    edit: '/api/v1/teams/{teamId}',
    readPermission: 'projects.read',
    managePermission: 'organization.manage',
    createPermission: 'projects.create',
    editPermission: 'projects.edit',
    fields: ['name', 'code', 'description', 'status', 'startDate', 'endDate'],
    statusKey: 'status',
    description:
      'Управляйте проектами независимо от отдела и сохраняйте историю завершённых проектов.',
  },
  {
    key: 'positions',
    section: 'organisation',
    title: 'Должности',
    singular: 'Должность',
    path: '/api/v1/positions' as GetPath,
    detail: '/api/v1/positions/{positionId}' as GetPath,
    idParam: 'positionId',
    create: '/api/v1/positions' as keyof paths,
    edit: '/api/v1/positions/{positionId}' as keyof paths,
    readPermission: 'positions.read',
    managePermission: 'organization.manage',
    createPermission: 'positions.create',
    editPermission: 'positions.edit',
    fields: ['name', 'code', 'description', 'status'],
    statusKey: 'status',
    description: 'Должности сотрудников с сохранением истории назначений.',
  },
  catalog('seniorities', 'Уровни', 'Уровень'),
  catalog('locations', 'Локации', 'Локация'),
  ...(['role-profiles', 'skills', 'employee-evaluation-templates'] as const).map((key) => ({
    key,
    section: 'performance' as const,
    title:
      key === 'role-profiles'
        ? 'Профили должностей'
        : key === 'skills'
          ? 'Навыки'
          : 'Шаблоны оценки',
    singular:
      key === 'role-profiles' ? 'Профиль должности' : key === 'skills' ? 'Навык' : 'Шаблон оценки',
    path: `/api/v1/${key}` as GetPath,
    detail: `/api/v1/${key}/{id}` as GetPath,
    create: `/api/v1/${key}` as keyof paths,
    edit: `/api/v1/${key}/{id}` as keyof paths,
    readPermission: 'performance_templates.read',
    managePermission: 'performance_templates.manage',
    fields: [
      'name',
      'code',
      'ownerEmployeeId',
      ...(key === 'role-profiles' ? ['positionId'] : []),
      'status',
    ],
    statusKey: 'status',
    description:
      'Управляйте версиями шаблонов для должностей и оценки сотрудников. Опубликованные версии сохраняются неизменными.',
    actions: (row: Row) =>
      field(row, 'status') === 'ACTIVE'
        ? [
            action(
              row,
              'Создать версию',
              `/api/v1/${key}/{id}/versions` as keyof paths,
              'performance_templates.manage',
            ),
          ]
        : [],
  })),
  {
    key: 'cycles',
    section: 'performance',
    title: 'Циклы оценки',
    singular: 'Цикл оценки',
    path: '/api/v1/performance-cycles',
    detail: '/api/v1/performance-cycles/{id}',
    create: '/api/v1/performance-cycles',
    edit: '/api/v1/performance-cycles/{id}',
    readPermission: 'performance_reviews.manage',
    managePermission: 'performance_reviews.manage',
    fields: ['name', 'status', 'periodStart', 'periodEnd', 'reviewDueAt'],
    statusKey: 'status',
    description: 'Задайте периоды и сроки оценки для рабочих планов и отзывов сотрудников.',
    actions: (row) =>
      field(row, 'status') === 'DRAFT'
        ? [
            action(
              row,
              'Открыть цикл',
              '/api/v1/performance-cycles/{id}/activate',
              'performance_templates.manage',
            ),
          ]
        : field(row, 'status') === 'ACTIVE'
          ? [
              action(
                row,
                'Закрыть цикл',
                '/api/v1/performance-cycles/{id}/close',
                'performance_templates.manage',
              ),
            ]
          : [],
  },
  {
    key: 'work-plans',
    section: 'performance',
    title: 'Рабочие планы',
    singular: 'Рабочий план',
    employeeScoped: true,
    path: '/api/v1/employees/{id}/work-plans',
    detail: '/api/v1/work-plans/{id}',
    create: '/api/v1/employees/{id}/work-plans',
    readPermission: 'work_plans.read',
    managePermission: 'work_plans.manage',
    fields: ['title', 'employeeId', 'cycleId', 'status', 'roleChanged'],
    statusKey: 'status',
    description:
      'Планируйте задачи сотрудника и отслеживайте прогресс по назначенному профилю должности.',
    actions: (row) => [
      ...(field(row, 'status') === 'DRAFT'
        ? [
            action(
              row,
              'Активировать план',
              '/api/v1/work-plans/{id}/activate',
              'work_plans.manage',
            ),
          ]
        : []),
      ...(field(row, 'status') === 'ACTIVE'
        ? [
            action(
              row,
              'Начать оценку',
              '/api/v1/work-plans/{id}/reviews',
              'performance_reviews.manage',
            ),
          ]
        : []),
    ],
  },
  {
    key: 'reviews',
    section: 'performance',
    title: 'Оценка сотрудников',
    singular: 'Оценка сотрудника',
    employeeScoped: true,
    path: '/api/v1/employees/{id}/reviews',
    detail: '/api/v1/reviews/{id}',
    readPermission: 'performance_reviews.read',
    managePermission: 'performance_reviews.manage',
    fields: ['employeeId', 'status', 'reviewDueAt', 'coverage', 'finalGrade'],
    statusKey: 'status',
    description:
      'Оценивайте задачи и навыки, собирайте самооценку и оценку руководителя, публикуйте результат.',
  },
  {
    key: 'accounts',
    section: 'admin',
    title: 'Учётные записи',
    singular: 'Учётная запись',
    path: '/api/v1/accounts',
    detail: '/api/v1/accounts/{accountId}',
    idParam: 'accountId',
    create: '/api/v1/accounts',
    readPermission: 'accounts.read',
    managePermission: 'accounts.manage',
    fields: ['login', 'status', 'employeeId', 'lastLoginAt'],
    statusKey: 'status',
    description: 'Приглашайте пользователей, связывайте профили сотрудников и управляйте доступом.',
    actions: (row) => [
      ...(field(row, 'status') === 'PENDING_ACTIVATION'
        ? [
            action(
              row,
              'Отправить приглашение',
              '/api/v1/accounts/{accountId}/activation-links',
              'accounts.manage',
            ),
          ]
        : []),
      ...(field(row, 'status') === 'ACTIVE'
        ? [
            action(
              row,
              'Отправить ссылку для сброса пароля',
              '/api/v1/accounts/{accountId}/password-reset-links',
              'accounts.manage',
            ),
            action(
              row,
              'Заблокировать учётную запись',
              '/api/v1/accounts/{accountId}/block',
              'accounts.manage',
            ),
          ]
        : []),
      ...(field(row, 'status') === 'BLOCKED'
        ? [
            action(
              row,
              'Разблокировать учётную запись',
              '/api/v1/accounts/{accountId}/unblock',
              'accounts.manage',
            ),
          ]
        : []),
    ],
  },
  {
    key: 'permission-groups',
    section: 'admin',
    title: 'Группы доступа',
    singular: 'Группа доступа',
    path: '/api/v1/permission-groups',
    detail: '/api/v1/permission-groups/{groupId}',
    idParam: 'groupId',
    create: '/api/v1/permission-groups',
    edit: '/api/v1/permission-groups/{groupId}',
    readPermission: 'permissions.manage',
    managePermission: 'permissions.manage',
    fields: ['name', 'createdAt'],
    description: 'Настраивайте разрешения и области доступа независимо от должностей сотрудников.',
  },
  {
    key: 'privacy-notices',
    section: 'admin',
    title: 'Уведомления о данных',
    singular: 'Уведомление об обработке данных',
    path: '/api/v1/privacy-notice-versions',
    create: '/api/v1/privacy-notice-versions',
    readPermission: 'privacy_notices.manage',
    managePermission: 'privacy_notices.manage',
    fields: ['jurisdiction', 'versionNumber', 'language', 'status', 'createdAt'],
    description: 'Подготовьте и опубликуйте уведомления об обработке данных кандидатов.',
    actions: (row) =>
      field(row, 'status') === 'DRAFT'
        ? [
            action(
              row,
              'Опубликовать уведомление',
              '/api/v1/privacy-notice-versions/{id}/publish',
              'privacy_notices.manage',
            ),
          ]
        : [],
  },
  {
    key: 'processing-policies',
    section: 'admin',
    title: 'Политики обработки',
    singular: 'Политика обработки',
    path: '/api/v1/processing-basis-policy-versions',
    create: '/api/v1/processing-basis-policy-versions',
    readPermission: 'processing_basis_policies.manage',
    managePermission: 'processing_basis_policies.manage',
    fields: ['legalApprovalReference', 'status', 'versionNumber', 'createdAt', 'rules'],
    description:
      'Настройте согласованные правила обработки данных по источнику кандидата, цели и юрисдикции.',
    actions: (row) =>
      field(row, 'status') === 'DRAFT'
        ? [
            action(
              row,
              'Активировать политику',
              '/api/v1/processing-basis-policy-versions/{id}/activate',
              'processing_basis_policies.manage',
              { effectiveAt: new Date().toISOString() },
            ),
          ]
        : [],
  },
  {
    key: 'audit',
    section: 'admin',
    title: 'Журнал аудита',
    singular: 'Событие аудита',
    path: '/api/v1/audit-events',
    readPermission: 'audit.read',
    fields: ['eventType', 'aggregateType', 'actorType', 'occurredAt', 'reason'],
    description: 'Просматривайте историю изменений и доступа к конфиденциальным данным.',
  },
];
applyBackendFeatures(resources);
export const sectionTitles: Record<Section, string> = {
  people: 'Сотрудники',
  recruitment: 'Подбор персонала',
  organisation: 'Организация',
  hr: 'HR-процессы',
  performance: 'Эффективность',
  admin: 'Администрирование',
};
export function findResource(section: string, key: string) {
  return resources.find((resource) => resource.section === section && resource.key === key);
}
